import { Autocomplete } from "@mui/material";
import { FC, useState } from "react";

import { NO_VALUE_DASH } from "@ryde/shared";

import { II18nKeyEntry } from "./custom-translations.models";
import { CustomTranslationOptionStyled } from "./custom-translations.styled";
import { TranslationNoOptions } from "./parts/translation-no-options";
import { TranslationPathInput } from "./parts/translation-path-input";

const MAX_VISIBLE_OPTIONS = 50;

interface ITranslationPathAutocompleteProps {
  entries: II18nKeyEntry[];
  isLoading: boolean;
  existingPaths: Set<string>;
  onPick: (entry: II18nKeyEntry) => void;
}

const filterEntries = (
  options: II18nKeyEntry[],
  rawQuery: string,
): II18nKeyEntry[] => {
  const query = rawQuery.trim().toLowerCase();

  if (!query) {
    return options.slice(0, MAX_VISIBLE_OPTIONS);
  }

  const matches: II18nKeyEntry[] = [];

  for (const option of options) {
    const isMatch =
      option.path.toLowerCase().includes(query) ||
      option.en.toLowerCase().includes(query) ||
      option.he.toLowerCase().includes(query);

    if (isMatch) {
      matches.push(option);

      if (matches.length >= MAX_VISIBLE_OPTIONS) {
        break;
      }
    }
  }

  return matches;
};

export const TranslationPathAutocomplete: FC<
  ITranslationPathAutocompleteProps
> = ({ entries, isLoading, existingPaths, onPick }) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);

  const trimmedInput = inputValue.trim();

  const handleAddNew = (): void => {
    if (!trimmedInput || existingPaths.has(trimmedInput)) {
      return;
    }

    // Create an override for a key that does not exist in the base bundle.
    // It only adds a custom translation; the original stays unknown ("—").
    onPick({ path: trimmedInput, en: "", he: "" });
    setInputValue("");
    setOpen(false);
  };

  return (
    <Autocomplete<II18nKeyEntry>
      data-e2e="custom-translations-path-picker"
      options={entries}
      loading={isLoading}
      value={null}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        if (reason !== "reset") {
          setInputValue(next);
        }
      }}
      blurOnSelect
      clearOnBlur
      handleHomeEndKeys
      getOptionLabel={option => option.path}
      getOptionDisabled={option => existingPaths.has(option.path)}
      filterOptions={(options, state) =>
        filterEntries(options, state.inputValue)
      }
      isOptionEqualToValue={(option, selected) => option.path === selected.path}
      noOptionsText={
        <TranslationNoOptions
          query={trimmedInput}
          alreadyExists={existingPaths.has(trimmedInput)}
          onAddNew={handleAddNew}
        />
      }
      onChange={(_, selected) => {
        if (selected) {
          onPick(selected);
          setInputValue("");
        }
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.path}>
          <CustomTranslationOptionStyled>
            <span className="ct-option__path">{option.path}</span>
            <span className="ct-option__values">
              <span className="ct-option__lang">EN</span>{" "}
              {option.en || NO_VALUE_DASH} &nbsp;·&nbsp;{" "}
              <span className="ct-option__lang">HE</span>{" "}
              {option.he || NO_VALUE_DASH}
            </span>
          </CustomTranslationOptionStyled>
        </li>
      )}
      renderInput={params => (
        <TranslationPathInput params={params} isLoading={isLoading} />
      )}
    />
  );
};
